import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, ChevronRight, Sun, Moon, Smartphone, MessageCircle, Activity, Eye, Ban,
  Film, Plane, MapPin, CreditCard, Lock, ShieldCheck, Info, AlertTriangle, MessagesSquare,
  FileText, LogOut, User, Trash2, HelpCircle,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { api } from "@/lib/api";
import { Loading } from "@/components/common";

const COUNTRIES = ["United States", "Canada", "United Kingdom", "United Arab Emirates", "Saudi Arabia", "Qatar", "Kuwait", "Malaysia", "Indonesia", "Pakistan", "India", "Turkey", "Egypt", "Nigeria", "France", "Germany", "Australia", "Other"];

function Section({ title, sub, children }) {
  return (
    <div style={{ marginTop: 26 }}>
      {title && <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 4 }}>{title}</div>}
      {sub && <div className="ss-muted" style={{ fontSize: 13, marginBottom: 10 }}>{sub}</div>}
      {!sub && title && <div style={{ height: 8 }} />}
      {children}
    </div>
  );
}

function Toggle({ on, onChange, testid }) {
  return (
    <button type="button" onClick={() => onChange(!on)} data-testid={testid} aria-pressed={on}
      style={{ width: 46, height: 26, borderRadius: 999, border: "none", cursor: "pointer", flexShrink: 0,
        background: on ? "var(--brand)" : "var(--line)", position: "relative", transition: "background-color .2s" }}>
      <span style={{ position: "absolute", top: 3, left: on ? 23 : 3, width: 20, height: 20, borderRadius: 999, background: "#fff", transition: "left .2s", boxShadow: "0 1px 3px rgba(0,0,0,.3)" }} />
    </button>
  );
}

function ToggleRow({ icon: Icon, label, desc, on, onChange, testid, last }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", borderBottom: last ? "none" : "1px solid var(--line)" }}>
      <span style={{ width: 38, height: 38, borderRadius: 999, background: "var(--surface-3)", display: "grid", placeItems: "center", flexShrink: 0 }}><Icon size={18} /></span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700 }}>{label}</div>
        {desc && <div className="ss-muted" style={{ fontSize: 13 }}>{desc}</div>}
      </div>
      <Toggle on={on} onChange={onChange} testid={testid} />
    </div>
  );
}

function NavRow({ icon: Icon, label, onClick, testid, last }) {
  return (
    <div onClick={onClick} data-testid={testid} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", cursor: "pointer", borderBottom: last ? "none" : "1px solid var(--line)" }}>
      <span style={{ width: 38, height: 38, borderRadius: 999, background: "var(--surface-3)", display: "grid", placeItems: "center", flexShrink: 0 }}><Icon size={18} /></span>
      <div style={{ flex: 1, fontWeight: 700 }}>{label}</div>
      <ChevronRight size={18} className="ss-muted" />
    </div>
  );
}

export default function Settings() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { mode, setMode } = useTheme();
  const [s, setS] = useState(null);

  useEffect(() => { api.get("/settings").then((d) => setS(d.settings)).catch(() => setS(null)); }, []);

  const update = async (patch) => {
    setS((prev) => ({ ...prev, ...patch }));
    try { await api.patch("/settings", patch); } catch (e) { toast.error(e.message || "Could not save"); }
  };
  const setTheme = (theme) => { setMode(theme); update({ theme }); };

  if (!user || !s) return <Loading />;
  const isAdmin = user.is_admin;

  const ThemeBtn = ({ val, icon: Icon, label }) => (
    <button onClick={() => setTheme(val)} data-testid={`theme-${val}`}
      className={`ss-btn ${mode === val ? "ss-btn-primary" : "ss-btn-ghost"}`}
      style={{ flex: 1, justifyContent: "center" }}><Icon size={16} /> {label}</button>
  );

  return (
    <div className="fade-up" style={{ maxWidth: 640, margin: "0 auto" }} data-testid="settings-page">
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
        <h1 style={{ fontSize: 26 }}>Account Settings</h1>
      </div>

      {/* Profile header */}
      <div className="ss-card" onClick={() => navigate("/profile")} style={{ padding: 16, marginTop: 14, display: "flex", alignItems: "center", gap: 14, cursor: "pointer" }} data-testid="settings-profile-card">
        <img src={user.avatar} alt="" style={{ width: 54, height: 54, borderRadius: 999, objectFit: "cover", border: "2px solid var(--gold)" }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 17 }}>{user.name}</div>
          <div style={{ color: "var(--brand)", fontWeight: 700, fontSize: 14 }}>View Profile</div>
        </div>
        <ChevronRight size={18} className="ss-muted" />
      </div>

      {/* Appearance */}
      <Section title="Appearance">
        <div style={{ display: "flex", gap: 8 }} data-testid="appearance-toggle">
          <ThemeBtn val="light" icon={Sun} label="Light" />
          <ThemeBtn val="dark" icon={Moon} label="Dark" />
          <ThemeBtn val="auto" icon={Smartphone} label="Auto" />
        </div>
      </Section>

      {/* Privacy */}
      <Section title="Privacy" sub="Select how you can interact with and be viewed by others.">
        <div className="ss-card" style={{ padding: 0, overflow: "hidden" }}>
          <ToggleRow icon={MessageCircle} label="Direct Messages" desc="Receive direct messages from other users." on={s.dm_enabled} onChange={(v) => update({ dm_enabled: v })} testid="toggle-dm" />
          <ToggleRow icon={Activity} label="Activity Status" desc="Share your activity with your friends." on={s.activity_status} onChange={(v) => update({ activity_status: v })} testid="toggle-activity" />
          <ToggleRow icon={Eye} label="Suggest account to others" desc="We'll suggest your account to your contacts." on={s.suggestible} onChange={(v) => update({ suggestible: v })} testid="toggle-suggest" />
          <NavRow icon={Ban} label="Blocked accounts" onClick={() => navigate("/profile")} testid="settings-blocked" last />
        </div>
      </Section>

      {/* Profile */}
      <Section title="Profile">
        <div className="ss-card" style={{ padding: 0, overflow: "hidden" }}>
          <ToggleRow icon={Film} label="Clips of you" desc="Show clips from your shows on your profile." on={s.clips} onChange={(v) => update({ clips: v })} testid="toggle-clips" />
          <ToggleRow icon={Plane} label="Vacation Mode" desc="Temporarily makes items in your store not purchasable." on={s.vacation_mode} onChange={(v) => update({ vacation_mode: v })} testid="toggle-vacation" last />
        </div>
      </Section>

      {/* Country */}
      <Section title="Country of Residence" sub="Influences show and product recommendations.">
        <div className="ss-card" style={{ padding: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "0 16px", height: 52 }}>
            <MapPin size={18} className="ss-muted" />
            <select value={s.country} onChange={(e) => update({ country: e.target.value })} data-testid="settings-country"
              style={{ flex: 1, border: 0, background: "transparent", font: "inherit", color: "inherit", outline: "none", height: "100%" }}>
              {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
      </Section>

      {/* General */}
      <Section title="General">
        <div className="ss-card" style={{ padding: 0, overflow: "hidden" }}>
          <NavRow icon={CreditCard} label="Payments & Shipping" onClick={() => navigate("/finances")} testid="settings-payments" />
          <NavRow icon={MapPin} label="Addresses" onClick={() => navigate("/addresses")} testid="settings-addresses" />
          <NavRow icon={Lock} label="Account" onClick={() => navigate("/profile")} testid="settings-account" />
          {isAdmin && <NavRow icon={ShieldCheck} label="Account Permissions" onClick={() => navigate("/admin")} testid="settings-permissions" />}
          <NavRow icon={Info} label="About SouqStream" onClick={() => navigate("/about")} testid="settings-about" last />
        </div>
      </Section>

      {/* Account (read-only) */}
      <Section title="Account">
        <div className="ss-card" style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "14px 16px", borderBottom: "1px solid var(--line)" }}>
            <span className="ss-muted">Name</span><b data-testid="settings-account-name">{user.name}</b>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "14px 16px" }}>
            <span className="ss-muted">Email</span><b data-testid="settings-account-email">{user.email}</b>
          </div>
        </div>
      </Section>

      {/* Help & Legal */}
      <Section title="Help & Legal">
        <div className="ss-card" style={{ padding: 0, overflow: "hidden" }}>
          {isAdmin && <NavRow icon={AlertTriangle} label="User Reports" onClick={() => navigate("/admin")} testid="settings-reports" />}
          <NavRow icon={MessagesSquare} label="Contact Us" onClick={() => navigate("/contact")} testid="settings-contact" />
          <NavRow icon={HelpCircle} label="Help & FAQ" onClick={() => navigate("/help")} testid="settings-help" />
          <NavRow icon={FileText} label="Terms & Privacy" onClick={() => navigate("/terms")} testid="settings-legal" />
          <NavRow icon={Trash2} label="Delete account" onClick={() => navigate("/delete-account")} testid="settings-delete-account" last />
        </div>
      </Section>

      <button className="ss-btn ss-btn-ghost ss-btn-block" style={{ color: "var(--red)", borderColor: "var(--red)", margin: "22px 0 40px" }} onClick={async () => { await logout(); navigate("/landing"); }} data-testid="settings-signout">
        <LogOut size={16} /> Sign Out
      </button>
    </div>
  );
}
